import { cloneDeep, isEmpty } from "lodash";
import { v4 as uuid } from "uuid";
import { getRandomLightRgba } from "../../utils/randomRgba";

export class FlowHandler {
  padding = 40;

  constructor({setNodes, setEdges, storeFlow}) {
    this.setNodes = setNodes;
    this.setEdges = setEdges;
    this.storeFlow = storeFlow;
  }

  getNodesSelected(nodes) {
    const nodesDefault = ["nodeStart", "nodeVariables"];
    let newNodes = cloneDeep(nodes);
    const selectedNodes = newNodes.filter((nds) => nds.selected && !nodesDefault.includes(nds.type));
    return selectedNodes;
  }

  handleGroup(nodes) {
    const nodesDefault = ["nodeStart", "nodeVariables"];
    let newNodes = cloneDeep(nodes);
    const selectedGroup = {};
    const selectedNodes = {};
    let removeGroups = [];
    const groups = {};
    const oldGroupPosition = {x: undefined, y: undefined};

    for (const node of newNodes) {
      if (node.selected && !nodesDefault.includes(node.type)) {
        if (node.type === "group") {
          selectedGroup[node.id] = node;
        } else {
          selectedNodes[node.id] = node;
        }
      }
    }

    for (const nodeId in selectedNodes) {
      const parent = selectedNodes[nodeId].parentNode;
      if (parent && selectedGroup[parent]) {
        delete selectedNodes[nodeId];
      } else if (parent && !selectedGroup[parent]) {
        return newNodes;
      }
    }

    const totalSelected = Object.keys(selectedNodes).length + Object.keys(selectedGroup).length;
    if (totalSelected < 2) {
      return nodes;
    }

    let group = null;
    if (Object.keys(selectedGroup).length === 1) {
      group = selectedGroup[Object.keys(selectedGroup)[0]];
      oldGroupPosition.x = group.position.x;
      oldGroupPosition.y = group.position.y;
    } else {
      removeGroups = Object.keys(selectedGroup);
      group = {
        id: uuid(),
        position: { x: 0, y: 0 },
        style: {
          width: 300,
          height: 300,
          border: 'none',
          borderRadius: 12,
          backgroundColor: getRandomLightRgba(),
        },
        type: 'group',
        resizing: true,
      }
    }

    const newNodePosition = { left: undefined, right: undefined, top: undefined, bottom: undefined };
    for (const item in selectedGroup) {
      const data = selectedGroup[item];
      if (!newNodePosition.left) {
        newNodePosition.left = data.position.x;
        newNodePosition.top = data.position.y;
        newNodePosition.right = data.position.x + data.width;
        newNodePosition.bottom = data.position.y + data.height;
      } else {
        newNodePosition.left = Math.min(newNodePosition.left, data.position.x);
        newNodePosition.top = Math.min(newNodePosition.top, data.position.y);
        newNodePosition.right = Math.max(newNodePosition.right, (data.position.x + data.width));
        newNodePosition.bottom = Math.max(newNodePosition.bottom, (data.position.y + data.height));
      }
    }
    for (const item in selectedNodes) {
      const data = selectedNodes[item];
      if (!newNodePosition.left) {
        newNodePosition.left = data.position.x;
        newNodePosition.top = data.position.y;
        newNodePosition.right = data.position.x + data.width;
        newNodePosition.bottom = data.position.y + data.height;
      } else {
        newNodePosition.left = Math.min(newNodePosition.left, data.position.x);
        newNodePosition.top = Math.min(newNodePosition.top, data.position.y);
        newNodePosition.right = Math.max(newNodePosition.right, (data.position.x + data.width));
        newNodePosition.bottom = Math.max(newNodePosition.bottom, (data.position.y + data.height));
      }
    }

    group.position.x = newNodePosition.left - this.padding;
    group.position.y = newNodePosition.top - this.padding;
    group.style.width = (newNodePosition.right - newNodePosition.left) + this.padding*2;
    group.style.height = (newNodePosition.bottom - newNodePosition.top) + this.padding*2;

    newNodes.splice(2, 0, group);

    for (const item of newNodes) {
      if (selectedGroup[item.parentNode] || selectedNodes[item.id]) {
        if (!item.parentNode) {
          item.position.x -= group.position.x;
          item.position.y -= group.position.y;
        } else if (group.id === item.parentNode && oldGroupPosition.x) {
          if (group.position.x < oldGroupPosition.x) {
            item.position.x += (oldGroupPosition.x - group.position.x);
          }
          if (group.position.y < oldGroupPosition.y) {
            item.position.y += (oldGroupPosition.y - group.position.y);
          }
        } else if (removeGroups.includes(item.parentNode)) {
          const oldGroup = selectedGroup[item.parentNode];
          item.position.x = (oldGroup.position.x - group.position.x) + item.position.x;
          item.position.y = (oldGroup.position.y - group.position.y) + item.position.y;
        }
        item.parentNode = group.id;
        item.expandParent = false;
        item.extent = 'parent';
      }
    }

    if (removeGroups.length > 0) {
      newNodes = newNodes.filter(item => !removeGroups.includes(item.id));
    }

    this.setNodes(newNodes);
  }

  handleUngroup(nodes, selectedId) {
    let newNodes = cloneDeep(nodes)
    const selectedGroup = {};
    const selectedNodes = {};
    const groups = {};

    let nodesSelected = this.getNodesSelected(newNodes);
    if (isEmpty(nodesSelected) && !selectedId) {
      return;
    }
    if (selectedId) {
      const nodeSelected = newNodes.find(n => n.id === selectedId);
      if (nodeSelected) {
        nodesSelected = [nodeSelected];
      }
    }

    for (const item of nodesSelected) {
      if (item.type === "group") {
        selectedGroup[item.id] = item;
      } else {
        selectedNodes[item.id] = item;
      }
    }

    const removeGroupIds = new Set();
    const ungroupId = new Set();

    for (const node of nodesSelected) {
      if (node.type === 'group') {
        removeGroupIds.add(node.id);
      } else if (node.parentNode) {
        let group = selectedGroup[node.parentNode] ?? groups[node.parentNode];
        if (!group) {
          group = newNodes.find(n => n.id === node.parentNode);
          groups[node.parentNode] = group;
        }

        ungroupId.add(node.parentNode);

        node.position.x += group.position.x;
        node.position.y += group.position.y;
        node.parentNode = null;
        node.extent = null;
      }
    }

    const groupChildCount = {};

    for (const node of newNodes) {
      if (node.type === "group") {
        if (!groupChildCount[node.id]) {
          groupChildCount[node.id] = 0;
        }
      }

      if (selectedGroup[node.parentNode]) {
        const group = selectedGroup[node.parentNode];
        node.position.x += group.position.x;
        node.position.y += group.position.y;
        node.parentNode = null;
        node.extent = null;
      }

      if (node.parentNode) {
        groupChildCount[node.parentNode] += 1;
      }
    }

    for (const grId in groupChildCount) {
      if (groupChildCount[grId] === 0) {
        removeGroupIds.add(grId);
      }
    }

    if (removeGroupIds.size > 0) {
      newNodes = newNodes.filter(item => !removeGroupIds.has(item.id));
    }

    this.setNodes(newNodes);
  }

  handleCopy({nodes, edges, variablesContext}) {
    const nodesDefault = ["nodeStart", "nodeVariables", "nodeComment", "group"];
    const cloneNodes = cloneDeep(nodes);
    let nodesCopySource = cloneNodes.filter(
      (nds) => nds.selected && !nodesDefault.includes(nds.type)
    );
    const edgeToCopied = edges.filter((nds) => nds.selected);
    if (!isEmpty(nodesCopySource)) {
      const nodesCloneFromSource = [];
      const oldNodeIdMap = {};
      nodesCopySource.forEach(node => {
        const newNode = cloneDeep(node);
        newNode.id = uuid();
        oldNodeIdMap[node.id] = newNode.id;
        const comments = variablesContext.comment;
        if (comments && typeof comments === 'object') {
          const comment = comments[node.id];
          if (comment) {
            newNode.data.comment = comment;
          }
        }
        if (newNode.parentNode) {
          const parent = nodes.find(i => i.id === newNode.parentNode);
          if (parent) {
            newNode.position.x += parent.position.x
            newNode.position.y += parent.position.y
          }
          newNode.parentNode = null;
          newNode.extent = null;
        }
        nodesCloneFromSource.push(newNode);
      });
      const edgeCloneFromSource = [];
      edgeToCopied.forEach((edge) => {
        const newEdge = cloneDeep(edge);
        for (const key in newEdge) {
          for (const oldUuid in oldNodeIdMap) {
            if (newEdge[key].includes && newEdge[key].includes(oldUuid)) {
              newEdge[key] = newEdge[key].replace(oldUuid, oldNodeIdMap[oldUuid])
            }
          }
        }
        edgeCloneFromSource.push(newEdge);
      })
      this.storeFlow.current = {
        node: nodesCloneFromSource,
        edge: edgeCloneFromSource,
      };
      localStorage.setItem('NodeDraft', JSON.stringify({
        node: nodesCloneFromSource,
        edge: edgeCloneFromSource,
      }));
    }
  }
}
